import {
  Component,
  inject,
  NgZone,
  OnInit,
  AfterViewInit,
  OnDestroy,
} from '@angular/core';
import * as MAP_CONFIG from './view-live-tracking.config';
import { GoogleMapsService } from 'src/app/shared/service/google-maps-service.service';
import { LoadingService } from 'src/app/core/services/loading.service';
import LatLngLiteral = google.maps.LatLngLiteral;
import { SsoService } from 'src/app/core/services/sso.service';
import { live_tracking } from 'src/app/shared/api.constant';
import { ActivatedRoute, Router } from '@angular/router';
import { LiveShare, VehicleInfo } from './view-live-tracking.interface';
import { interval, Subscription } from 'rxjs';
import { MarkerClusterer } from '@googlemaps/markerclusterer';
import { PrimecustomModule } from 'src/app/shared/primeng.custom.module';
import { ToastMessageOptions } from 'primeng/api';
import { HttpErrorResponse } from '@angular/common/http';
import { ServerEventService } from 'src/app/core/services/server-event.service';
import { AuthenticationService } from '../../../shared/service/authentication.service';
import { MapTypeViewComponent } from 'src/app/shared/component/map-type-view/map-type-view.component';
import { LIVE_SHARE } from 'src/app/shared/routeLink.constant';
import { allVariants, lob } from '../tracking.config';
import { VehicleColorConfigService } from 'src/app/shared/service/vehicle-color-config.service';
import { SharedModule } from 'src/app/shared/shared.module';
import { Store } from '@ngrx/store';
import { selectSelectedVehicle } from 'src/app/store/user/user.selectors';

@Component({
  selector: 'app-view-live-tracking',
  templateUrl: './view-live-tracking.component.html',
  styleUrls: ['./view-live-tracking.component.scss'],
  standalone: true,
  imports: [SharedModule, PrimecustomModule, MapTypeViewComponent],
})
export class ViewLiveTrackingComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  map!: any;
  mapOptions: google.maps.MapOptions = MAP_CONFIG.mapOptions;
  mapsApiLoaded!: boolean;
  mapCenter!: LatLngLiteral;
  private readonly googleMapsService = inject(GoogleMapsService);
  vehicleKey = '';
  vehicleInfo: VehicleInfo[] = [];
  expirationTime: Date = new Date();
  vehicleNumber = ''; //selected vehicle on map
  vehicleTypeCode = '';
  lob: any = lob;
  status = 'Running';
  model = 'RHINO LITE';
  reportingTime: any = new Date();
  private subscription!: Subscription;
  selectedVehicle: any;
  timeLeft: { days: string; hours: string; minutes: string; seconds: string } =
    {
      days: '00',
      hours: '00',
      minutes: '00',
      seconds: '00',
    };
  markerCluster: any;
  markerReference: any = [];
  polylineReference: any = [];
  polyPath!: any;
  showRefreshButton = false; // Show refresh button on error
  polyLines: any = [];

  markers: google.maps.marker.AdvancedMarkerElement[] = [];
  currentInfoWindow: google.maps.InfoWindow | null = null;
  infoWindowdata: {
    soc: number;
    odometer: number;
    vehicle_code: string;
    vehicle_number: string;
    speed: number;
    geo_lat: number;
    geo_long: number;
    status: string;
    unit: string;
  } = {
    soc: 0,
    odometer: 0,
    vehicle_code: '-',
    vehicle_number: '-',
    speed: 0,
    geo_lat: 0,
    geo_long: 0,
    status: '-',
    unit: '-',
  };
  isLoggedIn = false;

  isError = false;
  messages!: ToastMessageOptions[];

  isLoaded = false;
  selectedVehicleName = '';
  trafficLive = true;

  constructor(
    private readonly zone: NgZone,
    public loadingService: LoadingService,
    private readonly sso: SsoService,
    private readonly activeRoute: ActivatedRoute,
    private readonly sseService: ServerEventService,
    private readonly route: Router,
    private readonly batteryColorService: VehicleColorConfigService,
    private readonly AuthenticationService: AuthenticationService,
    private readonly store: Store
  ) {}
  ngOnInit(): void {
    this.store.select(selectSelectedVehicle).subscribe((vehicle) => {
      this.selectedVehicle = vehicle;
      this.selectedVehicleName = vehicle?.name ?? 'Truck';
    });

    this.selectedVehicle = this.selectedVehicle?.name ?? 'Truck';
    this.selectedVehicle = this.selectedVehicle.toLowerCase();
    this.mapCenter = this.googleMapsService.getCurrentPosition();
    this.activeRoute.paramMap.subscribe((params) => {
      this.vehicleKey = params.get('vehicleTrackerKey') ?? '';
    });

    // Handle errors
    this.sseService.error$.subscribe((error: string) => {
      this.showRefreshButton = true; // Show refresh button on error
      this.sseService.closeEventSource();
      this.loadingService.hide();
    });
    this.AuthenticationService.currentAuthStatus.subscribe((user) => {
      if (user) {
        this.isLoggedIn = true;
      } else {
        this.isLoggedIn = false;
      }
    });
    this.loadVehicles();
    this.isError = false;
  }

  loadVehicles() {
    this.loadingService.show();
    this.sso
      .getSSEURL(live_tracking + '/' + this.vehicleKey)

      .subscribe({
        next: (ele: LiveShare) => {
          if (ele.status) {
            const res = ele.data.response;
            this.vehicleInfo = res.vehicleInfo;
            this.expirationTime = res.expiryTime;
            this.vehicleTypeCode = ele.data.response.vehicleTypeCode;
            this.startCountdown();
            if (this.vehicleInfo.length && this.vehicleInfo.length != 1) {
              this.plotOnMap();
            }
            if (this.vehicleInfo.length && this.vehicleInfo.length == 1) {
              this.redirectToVehicleLiveTracking(this.vehicleInfo[0].regNum);
            }
            this.isError = false;
          } else {
            this.reset(ele.data.message);
          }
          this.loadingService.hide();

          this.isLoaded = true;
        },
        error: (error: HttpErrorResponse) => {
          this.loadingService.hide();
          this.isLoaded = true;

          if (!error.error?.status) {
            this.reset(error.error?.data?.message);
          }
        },
      });
  }

  reset(message: string) {
    this.vehicleNumber = '';
    this.status = '';
    this.model = '';
    this.reportingTime = '';
    this.messages = [{ severity: 'warn', summary: message }];
    this.isError = true;
  }

  ngAfterViewInit(): void {
    this.zone.runOutsideAngular(() => {
      this.googleMapsService
        .loadAPI()
        .then(() => {
          this.mapsApiLoaded = true;
          this.loadMap();
        })
        .catch(() => (this.mapsApiLoaded = false));
    });
  }

  loadMap() {
    const zoomControlOptions = {
      position: google.maps.ControlPosition.TOP_RIGHT,
    };
    this.mapOptions = {
      ...this.mapOptions,
      zoomControlOptions: zoomControlOptions,
    };
    this.map = new google.maps.Map(
      document.getElementById('live-map') as HTMLElement,
      this.mapOptions
    );

    this.loadingService.show();
  }

  plotOnMap(): void {
    this.removeAllMarkers();
    const latLngBounds = new google.maps.LatLngBounds();

    if (this.vehicleInfo) {
      const BATCH_SIZE = 500; // Adjust batch size to balance performance
      const processBatch = (start: number) => {
        const batch = this.vehicleInfo.slice(start, start + BATCH_SIZE);
        batch.forEach((element) => {
          const latLng = {
            lat: element.lat,
            lng: element.lon,
          };

          // Get the marker icon
          const markerImage = this.getMarkerIcon(element);

          // Create an AdvancedMarkerElement without rendering to the map immediately
          const marker = new google.maps.marker.AdvancedMarkerElement({
            position: latLng,
            title: element.regNum,

            content: this.createMarkerContent(
              markerImage,
              0,
              0,
              element.regNum
            ), // Custom marker content
          });

          // Push markers to the array but don't add to the map directly
          this.markers.push(marker);
          latLngBounds.extend(latLng);
          marker.addListener('click', () => {
            this.redirectToVehicleLiveTracking(element.regNum);
          });
        });

        // Process the next batch if available
        if (start + BATCH_SIZE < this.vehicleInfo.length) {
          setTimeout(() => processBatch(start + BATCH_SIZE), 0); // Yield control back to the browser
        } else {
          // Once all batches are processed, cluster the markers and fit bounds
          this.markerCluster = this.createMarkerClustering(
            this.map,
            this.markers
          );
          this.map.fitBounds(latLngBounds);
          this.loadingService.hide();
        }
      };
      processBatch(0);
    }
  }

  redirectToVehicleLiveTracking(vehicleNumber: string) {
    this.route.navigateByUrl(
      `${LIVE_SHARE}/${this.vehicleKey}/${vehicleNumber}`
    );
  }
  updateInfoWindow(regNum?: any) {
    if (this.currentInfoWindow) {
      this.currentInfoWindow.close(); // Close the current info window if it exists
    }
    const marker = this.markers.find((ele) => ele.title == regNum);
    const infowindow = this.createInfoWindow(this.infoWindowdata);
    infowindow.open(this.map, marker);
    this.currentInfoWindow = infowindow;
  }

  removeAllMarkers() {
    // Iterate over the markers array and remove each marker from the map
    this.markerReference.forEach((marker: any) => {
      marker.setMap(null);
    });
    this.markerReference = [];
    if (this.polyPath) {
      this.polyPath.setMap(null);
    }
    if (this.markerCluster) {
      this.markerCluster.clearMarkers(); // Clear previous clusters
    }
  }

  getMarkerIcon(element?: any): string {
    const vehicleName = this.lob
      .find((ele: any) => ele.code == this.vehicleTypeCode)
      ?.name?.toLowerCase();
    const status = element?.status?.toLowerCase();
    const modelPart =
      element.variantCode && allVariants?.includes(element.variantCode)
        ? `${element.variantCode}/`
        : '';
    return `assets/img/${vehicleName}/${modelPart}${status}.svg`;
  }
  createMarkerClustering(
    map: google.maps.Map,
    markers: google.maps.marker.AdvancedMarkerElement[]
  ): MarkerClusterer {
    return new MarkerClusterer({
      map,
      markers,
    });
  }
  createMarkerContent(
    markerImage: string,
    index?: number,
    size?: number,
    vehicleNumber?: string
  ): HTMLElement {
    if (size) {
      const img = document.createElement('img');
      img.src = markerImage;
      img.style.cssText =
        index == 0 || index == size - 1
          ? 'width:40px;height:50px'
          : 'width:11px;height:11px';

      return img;
    } else {
      const div = document.createElement('div');
      div.style.textAlign = 'center';

      // Create image element
      const img = document.createElement('img');
      img.src = markerImage;
      img.style.width = '40px';
      img.style.height = '40px';

      // Create vehicle number label
      const label = document.createElement('div');
      label.innerText = vehicleNumber ?? '';
      label.style.fontSize = '12px';
      label.style.marginBottom = '5px';
      label.style.color = 'white';
      label.style.backgroundColor = 'black';
      label.style.padding = '2px 5px';
      label.style.borderRadius = '5px';
      label.style.fontWeight = '600';

      // Append image and label to div
      div.appendChild(label);
      div.appendChild(img);

      return div;
    }
  }

  createInfoWindow(data: any) {
    const infoWindow = new google.maps.InfoWindow({
      content: this.InfoWindowTemplate({
        ...data,
        address: 'Loading address...',
      }),
      ariaLabel: data.vehicleNumber,
      minWidth: 220,
    });

    // Fetch address and update content later
    MAP_CONFIG.getAddress(data.latitude, data.longitude).then(
      (address: string) => {
        const updatedContent = this.InfoWindowTemplate({
          ...data,
          address,
        });
        infoWindow.setContent(updatedContent);
      }
    );

    return infoWindow;
  }

  InfoWindowTemplate(data: any) {
    const vehicleStatusAndColor = MAP_CONFIG.statusColor(data?.status);
    const batteryColor = this.batteryColorService.batteryPercentColor(
      data.soc,
      this.selectedVehicleName
    );
    const contentString = MAP_CONFIG.infoWindow(
      vehicleStatusAndColor,
      data,
      batteryColor
    );
    return contentString;
  }

  private startCountdown(): void {
    this.subscription = interval(1000).subscribe(() => {
      this.calculateTimeLeft();
    });
  }

  private calculateTimeLeft(): void {
    const now = new Date().getTime();
    const expiry = new Date(this.expirationTime).getTime();
    const difference = expiry - now;

    if (difference <= 0) {
      this.subscription.unsubscribe();
      this.timeLeft = { days: '00', hours: '00', minutes: '00', seconds: '00' };
    } else {
      const days = Math.floor(difference / (1000 * 60 * 60 * 24));
      const hours = Math.floor(
        (difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
      );
      const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((difference % (1000 * 60)) / 1000);

      this.timeLeft = {
        days: this.formatNumber(days),
        hours: this.formatNumber(hours),
        minutes: this.formatNumber(minutes),
        seconds: this.formatNumber(seconds),
      };
    }
  }

  private formatNumber(num: number): string {
    return num < 10 ? `0${num}` : `${num}`;
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
      this.sseService.closeEventSource();
    }
  }
}
